import { useIsFocused } from '@react-navigation/native'
import { StackScreenProps } from '@react-navigation/stack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { Button, Icon } from 'react-native-paper'
import Toast from 'react-native-root-toast'
import { useAuthContext } from '../../../../contexts/auth-context'
import Row from '../../../../@ui/containers/grid/row'
import { ShadowContainer } from '../../../../@ui/containers/cards/shadow.container'
import { Copyright } from '../../../../@ui/typo/copyright.typo'
import EagleView from '../../replay/shared/eagle-view'
import { LevelIndicator } from '../shared/LevelIndicator'
import MButton, {
  MButtonEventIndicator,
} from '../../../../@ui/buttons/m-button'
import { useNode } from '../../../../contexts/current-node.provider'
import {
  createStyle,
  useGlobalStyles,
} from '../../../../contexts/global-styles.provider'
import { useTheme } from '../../../../hooks/theme.hook'
import { useWechatShareInit } from '../../../../hooks/wechat-share.hook'
import { AppNavigatorParams } from '../../../../navigation/types'
import { SocketEvents, useSocketRoom } from '../../../../socket/socket.hooks'
import { IconAdd } from '../../../../utils/meldd-icons'
import { TargetType } from '../../perspectives/perspective-cards/types'
import { TaskPendingIcon } from '../../../tasks/components/task-pending-icon'
import { MelddTaskTargetType } from '../../../tasks/task.types'
import * as Sentry from '@sentry/react-native'
import { TutorialWrapper as TutorialComponent } from '../../../../@ui/containers/others/tutorial/tutorial.wrapper'
import { Tutorial } from '../../../../@ui/containers/others/tutorial/tutorial'
import { TutorialKey } from '../../../../@ui/containers/others/tutorial/tutorial.storage'
import { MelddSearchBar } from '../../../../@ui/form/meldd-search.input'
import { IconFilter } from '../../../../utils/meldd-icons'
import { useClusterListLazyQuery } from '../../../../generated/graphql'
import { useIsMobileDevice } from '../../../../hooks/is-mobile.hook'
import { NoDataMessage } from '../../../../@ui/containers/lists/no-data'
import ClusterList from './components/cluster.list'
// import { ReplayButton } from '../../replay/components/reply.button' // TODO: ReplayButton component needs to be created or located
import { TouchableIconWithToolTip } from '../../../../utils/meldd-tooltip'
import { ReplayButton } from '../../replay/components/reply.button'
const ClusterListTutorial = () => {
  const { t } = useTranslation('tutorials')

  return (
    <Tutorial
      steps={[{ tutorialId: 'create_cluster', message: t('create_cluster') }]}
      tutorialKey={TutorialKey.CLUSTER_SCREEN}
    />
  )
}

export function ClusterListScreen({
  navigation,
  route,
}: StackScreenProps<AppNavigatorParams, 'ClusterList'>) {
  const isFocused = useIsFocused()
  const { params } = route
  const { t } = useTranslation('clusters')
  const globalStyles = useGlobalStyles()
  const { authAction } = useAuthContext()
  const nodeId = params?.nodeId || ''
  const [eagleViewOpen, setEagleViewOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('eagle-view') === 'true'
    ) {
      setEagleViewOpen(true)
    }
  }, [])

  const styles = useStyle()
  const [doLoadClusters, { data, loading }] = useClusterListLazyQuery()

  const { setCurrentNode, currentNode } = useNode()
  const isMobile = useIsMobileDevice()

  const refresh = useCallback(
    async ({ searchQuery = '' }: { searchQuery?: string }) => {
      if (!nodeId) {
        navigation.navigate('ListNodes')
        return
      }
      try {
        await doLoadClusters({
          variables: { input: { nodeId, searchQuery } },
          fetchPolicy: 'cache-and-network',
        })
        setCurrentNode(nodeId)
      } catch (e) {
        console.error(e)
        Toast.show((e as Error).message)
        Sentry.captureException(e)
      }
    },
    [nodeId]
  )

  const refreshRoom = useCallback(() => {
    if (!nodeId) {
      return
    }
    refresh({ searchQuery })
  }, [searchQuery])

  useSocketRoom(nodeId).event(SocketEvents.Refresh, refreshRoom)

  useEffect(() => {
    if (isFocused) {
      refresh({})
    }
  }, [isFocused, refresh])

  useEffect(() => {
    setSearchInput('')
    setSearchQuery('')
  }, [nodeId])

  const theme = useTheme()

  const totalArticles = useMemo(() => {
    return data?.availableClusters?.reduce(
      (acc, cluster) => acc + cluster.articlesCount,
      0
    )
  }, [data])

  const totalVotes = useMemo(() => {
    return data?.availableClusters?.reduce(
      (acc, cluster) => acc + cluster.votesCount,
      0
    )
  }, [data])

  useWechatShareInit(() => {
    if (!nodeId || !data) return null

    const hasPerspectives = (totalArticles || 0) > 0
    const hasVotes = (totalVotes || 0) > 0

    if (!hasPerspectives || !hasVotes) {
      return {
        title: currentNode?.name || '',
      }
    }

    return {
      title: currentNode?.name || '',
      description: `${data.availableClusters.length} cluster(s), with ${totalArticles} perspective(s), and ${totalVotes} vote(s)`,
    }
  }, [nodeId, data, totalArticles, totalVotes])

  return (
    <View style={[globalStyles.pageContainer]}>
      <ShadowContainer shadowType="bottom">
        <Row justifyContent={'space-between'} alignItems={'center'}>
          {/* TOP LEFT */}
          <Row alignItems={'center'} columnGap={4} flex={1}>
            <TutorialComponent tutorialId="create_cluster">
              <Button
                icon={IconAdd}
                mode="elevated"
                style={styles.createButton} // fix the width for the tutorial highlight
                onPress={() =>
                  authAction(navigation, () =>
                    navigation.push('CreateCluster', {
                      nodeId: nodeId,
                    })
                  )
                }
              >
                {isMobile ? t('create') : t('Create cluster')}
              </Button>
            </TutorialComponent>
          </Row>

          {/* TOP RIGHT */}
          <Row
            alignItems={'center'}
            columnGap={2}
            style={{ marginVertical: theme.spacing(4) }}
          >
            <Row flex={1} justifyContent={'flex-end'}>
              <LevelIndicator
                target={TargetType.Node}
                targetId={nodeId}
                flat={true}
                onEagleViewClick={() => setEagleViewOpen(!eagleViewOpen)}
                eagleViewOpen={eagleViewOpen}
              />
            </Row>

            <TaskPendingIcon
              targetId={nodeId}
              targetType={MelddTaskTargetType.NODE_TASK}
              size={22}
            />
            {nodeId && (
              <ReplayButton targetId={nodeId} targetType={TargetType.Node} />
            )}
            {currentNode?.isAdmin && (
              <TouchableIconWithToolTip
                tooltip={t('node-settings-tooltip')}
                onPress={() =>
                  navigation.navigate('NodeSettings', {
                    nodeId: currentNode.id,
                  })
                }
                icon={<Icon source="cog-outline" size={22} />}
                mode="text"
              />
            )}
          </Row>
        </Row>
        <Row>
          <View style={{ flex: 1 }}>
            <MelddSearchBar
              icon={IconFilter}
              placeholder={t('search-clusters')}
              onChangeText={setSearchInput}
              onSearch={async (searchQuery) => {
                await refresh({ searchQuery })
                setSearchQuery(searchQuery)
              }}
              value={searchInput}
            />
          </View>
        </Row>
      </ShadowContainer>

      <ClusterList
        loading={loading}
        onRefresh={() => refresh({ searchQuery })}
        data={data?.availableClusters || []}
        onClusterSelected={(clusterId) => {
          navigation.push('ClusterCards', { clusterId, nodeId })
        }}
        onClusterContributionsPress={(clusterId) => {
          navigation.push('ClusterContributions', { clusterId })
        }}
        NoData={
          <NoDataMessage
            onSearch={() =>
              navigation.navigate('Search', { nodeId, searchQuery })
            }
            onCreate={() => navigation.navigate('CreateCluster', { nodeId })}
            labels={{
              noDataMessage: t('no-clusters-found'),
              createMessage: t('create-cluster'),
              searchMessage: t('make-a-global-search'),
            }}
          />
        }
      />

      <EagleView
        targetId={nodeId}
        open={eagleViewOpen}
        onClose={() => setEagleViewOpen(false)}
        shareParams={
          nodeId ? { targetType: TargetType.Node, targetId: nodeId } : undefined
        }
      />
      {!eagleViewOpen && (
        <>
          <MButtonEventIndicator nodeId={nodeId} />
          <MButton
            targetId={nodeId}
            targetType={MelddTaskTargetType.NODE_TASK}
            preview={currentNode?.name || ''}
          />
        </>
      )}
      <Copyright />
      <ClusterListTutorial />
    </View>
  )
}
export default ClusterListScreen

const useStyle = createStyle(({ theme, isMobile }) => ({
  createButton: {
    width: '100%',
  },
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4),
    paddingRight: 0,
    borderBottomColor: theme.colors.surfaceVariant,
    borderBottomWidth: 1,
  },
  info: {
    rowGap: theme.spacing(2),
    flex: 1,
  },
  topic: {},
  infoIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  icon: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing(2),
    width: theme.spacing(16),
  },
  fab: {
    position: 'absolute',
    bottom: theme.spacing(12),
    right: theme.spacing(10),
  },
}))
